import { MouseEvent, useCallback, useRef } from "react";
import {
  getLayoutBrowseGroup,
  LayoutBrowseGroup,
} from "entities/configurator/model/layoutMeta";

export const useLayoutNavigationFocus = (
  group: LayoutBrowseGroup | null,
  doorCode: string | undefined,
) => {
  const pendingFocus = useRef<{
    source: HTMLButtonElement;
    target: string;
    doorCode: string | undefined;
    group: LayoutBrowseGroup | null;
  } | null>(null);

  const onClickCapture = (event: MouseEvent<HTMLElement>) => {
    pendingFocus.current = null;
    const source = (event.target as Element).closest<HTMLButtonElement>(
      "button[data-layout-group], button[data-layout-back]",
    );
    if (!source) return;

    const target = group
      ? `button[data-layout-group="${group}"]`
      : "button[data-layout-back]";
    pendingFocus.current = {
      source,
      target,
      doorCode,
      group: group ? null : getLayoutBrowseGroup(source.dataset.layoutGroup),
    };
  };

  const focusContent = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !pendingFocus.current) return;
      const pending = pendingFocus.current;
      if (pending.doorCode !== doorCode || pending.group !== group) {
        pendingFocus.current = null;
        return;
      }
      pendingFocus.current = null;

      const activeElement = node.ownerDocument.activeElement;
      // Do not reclaim focus if the user moved elsewhere during navigation.
      if (
        activeElement !== node.ownerDocument.body &&
        activeElement !== pending.source
      )
        return;

      const target = node.querySelector<HTMLButtonElement>(pending.target);
      (target ?? node).focus();
    },
    [doorCode, group],
  );

  return { focusContent, onClickCapture };
};
